import React, { useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Checkbox from '@mui/material/Checkbox'
import TextField from '@mui/material/TextField'
import ListItemText from '@mui/material/ListItemText'
import Box from '@mui/material/Box'
import { FixedSizeList } from 'react-window'
import { TagStyled } from './styled'

type IItemOption = {
  value: string | number
  label: string
}

interface IsSelectProps {
  label: string
  error?: string | undefined
  value: Array<string | number> | null
  onChange?: (value: Array<string | number>) => void
  width?: string
  maxWidth?: string
  options: IItemOption[]
  style?: React.CSSProperties
  onBlur?: () => void
  disabled?: boolean
  limitTags?: number
  noChip?: boolean
  showCheckboxes?: boolean
}

const ListboxComponent = React.forwardRef<HTMLDivElement, any>(function ListboxComponent({ children, ...other }, ref) {
  const height = children.length > 10 ? 380 : children.length > 5 ? 300 : 240
  return (
    <div ref={ref} {...other} style={{ overflow: 'auto', maxHeight: 380, padding: '8px' }}>
      <FixedSizeList height={height} itemCount={React.Children.count(children)} itemSize={64} width="100%">
        {({ index, style }) => {
          const child = React.Children.toArray(children)[index]
          if (!React.isValidElement(child)) return null
          return (
            <div style={style} key={child.key}>
              {child}
            </div>
          )
        }}
      </FixedSizeList>
    </div>
  )
})

export default function MultiSelect(props: IsSelectProps) {
  const {
    label,
    error,
    value = [],
    options = [],
    onChange,
    onBlur,
    width,
    disabled,
    limitTags,
    noChip,
    maxWidth,
    showCheckboxes = true,
  } = props

  const [inputValue, setInputValue] = useState<string>('')

  const handleChange = (event: any, newValue: Array<string | number> | IItemOption[]) => {
    if (onChange) {
      const selectedValues: Array<string | number> = newValue
        ? Array.isArray(newValue) && typeof newValue[0] === 'object'
          ? (newValue as IItemOption[]).map((option) => option.value) // Mapeamos a los valores
          : (newValue as Array<string | number>) // Ya es un array de string | number
        : []
      onChange(selectedValues)
      setInputValue('')
    }
  }

  const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase()))

  const renderOption = (
    renderProps: any,
    option: IItemOption,
    { selected }: { selected: boolean }
  ): React.ReactNode => {
    return (
      <Box
        {...renderProps}
        key={`list-select-${option.label}-${option.value}`}
        component="li"
        sx={{
          display: 'flex',
          alignItems: 'center',
          alignContent: 'center',
          gap: 1,
          minHeight: '56px',
          padding: '10px 12px !important',
          marginBottom: '6px',
          borderRadius: '14px',
          backgroundColor: selected ? 'rgba(45, 116, 156, 0.10)' : '#ffffff',
          border: selected ? '1px solid rgba(45, 116, 156, 0.22)' : '1px solid transparent',
          transition: 'background-color 0.18s ease, border-color 0.18s ease',
          '&:last-child': {
            marginBottom: 0,
          },
          '&.Mui-focused': {
            backgroundColor: selected ? 'rgba(45, 116, 156, 0.14)' : 'rgba(45, 116, 156, 0.06)',
          },
          '& .MuiListItemText-primary': {
            fontSize: '13px',
            lineHeight: 1.4,
            color: '#24384d',
            fontWeight: selected ? 600 : 500,
            wordBreak: 'break-word',
          },
        }}
      >
        {showCheckboxes && (
          <Checkbox
            onChange={() => {
              const newSelectedValues = selected
                ? (value || []).filter((val) => val !== option.value)
                : [...(value || []), option.value]
              handleChange(null, newSelectedValues)
            }}
            checked={selected}
            size="small"
            sx={{
              padding: 0,
              marginRight: '6px',
              alignSelf: 'flex-start',
              marginTop: '2px',
              color: '#94a3b8',
              '&.Mui-checked': {
                color: '#2f7edb',
              },
            }}
          />
        )}
        <ListItemText primary={option.label} />
      </Box>
    )
  }

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      disabled={disabled}
      id="multi-select"
      options={filteredOptions}
      value={options.filter((option) => (Array.isArray(value) ? value.includes(option.value) : false))}
      getOptionLabel={(option) => option.label}
      onChange={handleChange}
      onBlur={onBlur}
      limitTags={limitTags ? limitTags : undefined}
      inputValue={inputValue}
      renderTags={
        noChip
          ? (tag) => {
              return (
                <TagStyled>
                  {tag
                    .filter((option) => option.value !== 0)
                    .map((option, index) => (
                      <div key={`multi-select-index-${index}`}>
                        {index === tag.length - 1 ? option.label : `${option.label}, `}
                      </div>
                    ))}
                </TagStyled>
              )
            }
          : undefined
      }
      renderOption={(properties, option, { selected }) => {
        return renderOption(properties, option, { selected })
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          placeholder="Buscar"
          error={!!error}
          helperText={error}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          sx={{
            '.MuiFormHelperText-root': {
              position: 'absolute',
              bottom: -24,
              borderRadius: '16px',
            },
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
          }}
        />
      )}
      ListboxComponent={ListboxComponent}
      sx={{
        backgroundColor: '#FFF',
        width: width || '100%',
        minWidth: '240px',
        maxWidth: maxWidth || '300px',
        boxShadow: '0px 10px 24px rgba(31, 33, 36, 0.08)',
        '.MuiOutlinedInput-notchedOutline': { border: 0 },
        borderRadius: '16px',
        '& .MuiAutocomplete-inputRoot': {
          minHeight: '52px',
          paddingLeft: '6px',
          paddingRight: '42px !important',
        },
        '& .MuiAutocomplete-popupIndicator': {
          color: '#6f8198',
        },
        '& .MuiAutocomplete-tag': {
          maxWidth: 'calc(100% - 12px)',
        },
        '& .MuiAutocomplete-endAdornment': {
          right: '10px',
        },
        '& .MuiInputLabel-root': {
          color: '#6f8198',
        },
      }}
      slotProps={{
        paper: {
          sx: {
            marginTop: '10px',
            borderRadius: '18px',
            border: '1px solid rgba(45, 116, 156, 0.12)',
            boxShadow: '0px 18px 32px rgba(31, 33, 36, 0.12)',
            padding: '6px',
          },
        },
      }}
    />
  )
}
